import { useEffect } from "react";
import { useUser } from "@client/hooks/useAuth";
import { BRAND_HEX } from "@shared/colour";

/**
 * The tenant's brand colour, as identity: `--tenant` on the root.
 *
 * `tenants.brand` rides along in the session payload. Until the design pass (22/10) it replaced
 * the app's `--primary`, so the whole interface took the tenant's colour; now the chrome stays
 * ChatNutri's turquesa and the tenant's colour marks what is theirs — the avatar ring in the
 * header today, the patient-facing pages next (docs/15-backlog.md). Consumers read
 * `var(--tenant, var(--primary))`, so an account without a colour simply shows turquesa.
 *
 * A <style> element with `html:root`, as before: plain `:root` has the same specificity as
 * index.css, and which one won would come down to injection order.
 */
export function useBrandTheme() {
  const { data: user } = useUser();
  const raw = user?.tenant.brand?.colour;
  const colour = typeof raw === "string" && BRAND_HEX.test(raw) ? raw : null;

  useEffect(() => {
    // No colour, or an unparseable one: the identity accents fall back to turquesa.
    if (!colour) return;

    /*
     * Design pass, step 0 (22/10): the tenant's colour is identity, not chrome. It used to replace
     * `--primary`, so one account's green repainted every button, link and active tab, and the app
     * stopped looking like the product the landing page and the login sell. The chrome is
     * ChatNutri's now; the tenant's colour is `--tenant`, read by identity accents (the avatar
     * ring in the header, and the patient-facing pages as they adopt it).
     */
    const style = document.createElement("style");
    style.id = "brand-theme";
    style.textContent = `html:root { --tenant: ${colour}; }`;
    document.head.appendChild(style);
    return () => { style.remove(); };
  }, [colour]);
}
